import { createContext, useContext, type ReactNode } from "react";

/**
 * Moving between routes, supplied by the shell.
 *
 * Containers call this rather than touching `window.history` themselves, for
 * two reasons. The shell holds the current path in state, so a container that
 * pushed history directly would change the URL and leave the rendered screen
 * behind it. And a container under test needs to be able to assert *that* it
 * navigated and where, which a spy on this is able to do and a spy on the
 * history API is not.
 *
 * `replace` is for redirects: the page the operator was not meant to stay on
 * must not sit in the back stack.
 */
export type NavigateFn = (to: string, options?: { replace?: boolean }) => void;

const NavigateContext = createContext<NavigateFn | undefined>(undefined);

export function NavigateProvider({
  navigate,
  children,
}: {
  navigate: NavigateFn;
  children: ReactNode;
}) {
  return <NavigateContext.Provider value={navigate}>{children}</NavigateContext.Provider>;
}

export function useNavigate(): NavigateFn {
  const navigate = useContext(NavigateContext);
  if (!navigate) {
    throw new Error(
      "No navigate function in context. Wrap the tree in <NavigateProvider navigate={…}> — App supplies it.",
    );
  }
  return navigate;
}
